<template>
  <div class="member-activity">
    <div v-if="!loading && changes.length === 0" class="activity-empty">
      <span class="material-icons" aria-hidden="true">history</span>
      <span>{{ $t('memberActivity.empty') }}</span>
    </div>

    <template v-else-if="changes.length">
      <div class="activity-summary">
        {{ $t('memberActivity.changesCount', { count: changes.length }) }}
      </div>

      <div v-for="group in groups" :key="group.date" class="activity-group">
        <div class="activity-date">{{ group.date }}</div>
        <div class="activity-list">
          <div v-for="(change, i) in group.items" :key="`${change.changedAt}-${i}`" class="activity-row">
            <div class="activity-marker" aria-hidden="true">
              <span class="material-icons">edit</span>
            </div>
            <div class="activity-content">
              <div class="activity-heading">
                <span class="activity-field">{{ fieldLabel(change.fieldName) }}</span>
                <span class="activity-time">{{ formatTime(change.changedAt) }}</span>
              </div>
              <div class="activity-change">
                <span v-if="visibleValue(change, 'old')" class="activity-value old">
                  {{ visibleValue(change, 'old') }}
                </span>
                <span v-if="visibleValue(change, 'old')" class="material-icons activity-arrow" aria-hidden="true">
                  arrow_forward
                </span>
                <span class="activity-value new">{{ visibleValue(change, 'new') }}</span>
              </div>
              <div v-if="change.changedByName" class="activity-by">
                {{ $t('memberActivity.by') }} {{ change.changedByName }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaginationBar
        :total-pages="totalPages"
        :total-count="changes.length"
        :per-page.sync="perPage"
        :current-page="currentPage"
        @pagechanged="currentPage = $event"
      />
    </template>
  </div>
</template>

<script>
import membersService from '@/services/members.service';
import PaginationBar from '@/components/Pagination/PaginationBar.vue';

const FIELD_LABELS = {
  GivenName: 'memberActivity.fields.firstName',
  MiddleName: 'memberActivity.fields.middleName',
  FamilyName: 'memberActivity.fields.lastName',
  Email: 'memberActivity.fields.email',
  PhoneNumber: 'memberActivity.fields.phoneNumber',
  PhoneCountryCode: 'memberActivity.fields.phoneCountry',
  Gender: 'memberActivity.fields.gender',
  Birthday: 'memberActivity.fields.birthday',
  CountryOfResidence: 'memberActivity.fields.countryOfResidence',
  LinkedUserAccount: 'memberActivity.fields.linkedAccount',
};

export default {
  name: 'ActivityTab',
  components: { PaginationBar },
  props: {
    memberId: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      changes: [],
      loading: false,
      currentPage: 1,
      perPage: 10,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.changes.length / this.perPage);
    },
    pagedChanges() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.changes.slice(start, start + this.perPage);
    },
    groups() {
      const byDate = new Map();
      this.pagedChanges.forEach((change) => {
        const key = this.formatDate(change.changedAt);
        if (!byDate.has(key)) byDate.set(key, []);
        byDate.get(key).push(change);
      });
      return Array.from(byDate, ([date, items]) => ({ date, items }));
    },
  },
  watch: {
    perPage() {
      this.currentPage = 1;
    },
  },
  async created() {
    await this.getChanges();
  },
  methods: {
    async getChanges() {
      this.loading = true;
      this.$store.commit('changeShowLoader', true);
      try {
        const data = await membersService.getProfileChanges(this.memberId);
        this.changes = Array.isArray(data) ? data : [];
      } catch (e) {
        this.showError(e, this.$t('errors.entities.member'), 'load');
      } finally {
        this.$store.commit('changeShowLoader', false);
        this.loading = false;
      }
    },
    fieldLabel(field) {
      if (FIELD_LABELS[field]) return this.$t(FIELD_LABELS[field]);
      return (field || '').replace(/([a-z])([A-Z])/g, '$1 $2');
    },
    visibleValue(change, side) {
      const value = side === 'old' ? change.oldValue : change.newValue;
      if (change.fieldName === 'LinkedUserAccount') {
        if (side === 'old') return '';
        return value ? this.$t('memberActivity.accountLinked') : this.$t('memberActivity.accountUnlinked');
      }
      if (change.fieldName === 'Gender') {
        const normalized = String(value || '').toLowerCase();
        if (normalized === '1' || normalized === 'male' || normalized === 'm') return this.$t('memberActivity.male');
        if (normalized === '2' || normalized === 'female' || normalized === 'f') return this.$t('memberActivity.female');
      }
      if (change.fieldName === 'Birthday' && value) {
        return this.formatActivityDate(value);
      }
      if (/id$/i.test(change.fieldName || '')) {
        return side === 'old' ? '' : this.$t('memberActivity.updated');
      }
      return value === null || value === undefined || value === '' ? this.$t('generalMessages.NA') : value;
    },
    formatDate(iso) {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar' : 'en-GB', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    },
    formatActivityDate(value) {
      const datePart = String(value).split('T')[0];
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
      if (!match) return value;
      const [, year, month, day] = match;
      const monthIndex = Number(month) - 1;
      if (monthIndex < 0 || monthIndex > 11) return value;
      const calendarDate = new Date(Date.UTC(Number(year), monthIndex, Number(day), 12));
      return new Intl.DateTimeFormat(this.$i18n.locale === 'ar' ? 'ar' : 'en-GB', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
      }).format(calendarDate);
    },
    formatTime(iso) {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleTimeString(this.$i18n.locale === 'ar' ? 'ar' : 'en-GB', {
        hour: '2-digit', minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.member-activity { color: #343a40; margin-inline: auto; max-width: 48rem; width: 100%; }
.activity-summary { padding: 0 0 12px; color: #6c757d; font-size: 0.8125rem; }
.activity-group { margin-bottom: 18px; }
.activity-date { margin-bottom: 8px; color: #6c757d; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; }
.activity-list { border: 1px solid #e1e5eb; border-radius: 6px; background: #fff; }
.activity-row { display: grid; grid-template-columns: 34px minmax(0, 1fr); gap: 10px; padding: 14px 16px; border-bottom: 1px solid #e9ecef; }
.activity-row:last-child { border-bottom: 0; }
.activity-marker { display: flex; width: 30px; height: 30px; align-items: center; justify-content: center; border-radius: 50%; background: #e8f2ff; color: #007bff; }
.activity-marker .material-icons { font-size: 16px; }
.activity-heading { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; }
.activity-field { color: #212529; font-size: 0.875rem; font-weight: 600; }
.activity-time, .activity-by { color: #868e96; font-size: 0.75rem; }
.activity-change { display: flex; min-width: 0; align-items: center; gap: 8px; margin-top: 7px; }
.activity-value { max-width: 45%; overflow-wrap: anywhere; font-size: 0.8125rem; }
.activity-value.old { color: #6c757d; text-decoration: line-through; }
.activity-value.new { color: #218838; font-weight: 600; }
.activity-arrow { flex: 0 0 auto; color: #adb5bd; font-size: 16px; }
.activity-by { margin-top: 6px; }
.activity-empty { display: flex; min-height: 180px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #868e96; font-size: 0.875rem; }
.activity-empty .material-icons { color: #adb5bd; font-size: 32px; }
@media (max-width: 575px) {
  .activity-row { grid-template-columns: 28px minmax(0, 1fr); padding: 12px; }
  .activity-heading { align-items: flex-start; flex-direction: column; gap: 2px; }
  .activity-value { max-width: 42%; }
}
</style>
